import { createSelector } from 'reselect'

import { isAgeTeen } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { getIsMinorEAPSessionExhausted } from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/utils'
import { TriageScreenerField } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { SearcherType } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'
import { TriageScreenerNames } from '@lyrahealth-inc/shared-app-logic/src/models/triageScreener/TriageScreener'
import { User } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { getNumberOfSessions, getNumberOfSessionsByProgram } from '../../../data/customer/customerSelectors'
import { getMinorsSessionUsages } from '../../../data/minors/minorSelectors'
import { getUserRegisteredChildren } from '../../../data/user/userSelectors'

export const getTriageState = ($$state: $TSFixMe) => $$state.get('triage')

export const getHighRiskModalLevelShown = createSelector(getTriageState, ($$triageState) =>
  $$triageState.get('highRiskModalLevelShown'),
)

export const getAiHighRiskModalShownForSearchId = createSelector(getTriageState, ($$triageState) =>
  $$triageState.get('aiHighRiskModalShownForSearchId'),
)

export const getTriageScreeners = createSelector(getTriageState, ($$triageState) =>
  $$triageState.get('screeners').filter(($$screener: $TSFixMe) => $$screener.getIn(['data', 'mobileOnly'])),
)

export const getSearches = createSelector([getTriageState], ($$triageState) => $$triageState.get('searches'))

export const getRiskFactors = createSelector([getTriageState], ($$triageState) => $$triageState.get('riskFactors'))

export const getSelectedSearchId = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('selectedSearchId'),
)

export const getSelectedSearch = createSelector([getSearches, getSelectedSearchId], ($$searches, selectedSearchId) =>
  $$searches.find((search: $TSFixMe) => search?.get('id') === selectedSearchId),
)

const getSelectedSearchBody = createSelector([getSelectedSearch], ($$selectedSearch) => $$selectedSearch?.get('body'))

// Get the lyra id of the child that's selected for the current search
export const getChildLyraId = createSelector(
  [getSelectedSearchBody],
  (selectedSearchBody) => selectedSearchBody?.get('child_lyra_id') || selectedSearchBody?.get('childLyraId'),
)

export const getIsChildAccountClaimed = createSelector(
  [getChildLyraId, getUserRegisteredChildren],
  (childLyraId, registeredChildren) =>
    !!childLyraId && !!registeredChildren?.some((child: User) => child.lyraId === childLyraId),
)

// Return boolean if a child is selected for the current search
export const getIsChildSearchForCare = createSelector([getChildLyraId], (childId: string) => Boolean(childId))

const getSelectedSearchQuestionnaires = createSelector([getSelectedSearchBody], ($$body) =>
  $$body?.get('questionnaires'),
)

export const getSelectedSearchGender = createSelector([getSelectedSearchBody], ($$body) => $$body?.get('gender'))

export const getSelectedSearchPrimaryNeed = createSelector(
  [getSelectedSearchQuestionnaires],
  ($$questionnaires) =>
    $$questionnaires?.getIn([TriageScreenerNames.PRIMARY_NEEDS, TriageScreenerField.WHATS_ON_YOUR_MIND]) as
      | string
      | undefined,
)

export const getSelectedSearchPrimaryNeedMultiselect = createSelector(
  [getSelectedSearchQuestionnaires],
  ($$questionnaires) => $$questionnaires?.getIn([TriageScreenerNames.PRIMARY_NEEDS, TriageScreenerField.SELECTIONS]),
)

export const getSelectedSearchNeeds = createSelector(
  [getSelectedSearchQuestionnaires],
  ($$questionnaires) => $$questionnaires?.get(TriageScreenerNames.PRIMARY_NEEDS) as Dict,
)

export const getTriageQuestions = createSelector([getTriageState], (triageState) => triageState.get('triageQuestions'))

export const getLastTriageScreener = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('lastTriageScreener'),
)

export const getTreatmentRecommendations = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('treatmentRecommendations'),
)

export const getProgressBarPercentage = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('percentProgress'),
)

export const getShownTierPage = createSelector([getTreatmentRecommendations], ($$treatmentRecommendations) =>
  $$treatmentRecommendations?.get('shownTierPage'),
)

export const getUpdatingSearch = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('updatingSearch'),
)

export const getIsCreatingSearch = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('isCreatingSearch'),
)

export const getLoadingNextQuestion = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('loadingNextQuestion'),
)

export const getLoadingTreatmentRecommendations = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('loadingTreatmentRecommendations'),
)

// True while a search is being created or its treatment recommendations are being fetched
export const getIsTriageSearchInFlight = createSelector(
  [getIsCreatingSearch, getLoadingTreatmentRecommendations],
  (isCreatingSearch, loadingTreatmentRecommendations) => Boolean(isCreatingSearch || loadingTreatmentRecommendations),
)

export const getShowTriageHighAlertModal = createSelector([getTriageState], ($$triageState) =>
  $$triageState.getIn(['highAlert', 'showModal']),
)

export const getShowTriageHighAlertSI = createSelector([getTriageState], ($$triageState) =>
  $$triageState.getIn(['highAlert', 'isActiveSI']),
)

export const getIsAiTriageHighRisk = createSelector([getTriageState], ($$triageState) =>
  $$triageState.getIn(['highAlert', 'isAiTriageHighRisk']),
)

export const getAiRiskLevel = createSelector([getTriageState], ($$triageState) =>
  $$triageState.getIn(['highAlert', 'aiRiskLevel']),
)

export const getHighAlertCallbackRequested = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('highAlertCallbackRequested'),
)

export const getEligibleTreatmentOptions = createSelector([getTreatmentRecommendations], ($$treatmentRecommendations) =>
  $$treatmentRecommendations?.get('eligibleTreatmentOptions'),
)

export const getShouldCreateNewSearch = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('shouldCreateNewSearch'),
)

const getNavigationState = createSelector([getTriageState], ($$triageState) => $$triageState.get('navigationState'))

export const getHasResumedCompletedSearch = createSelector([getNavigationState], ($$navigationState) =>
  $$navigationState.get('hasResumedCompletedSearch'),
)

export const getUiBackFromTreatmentRecs = createSelector([getNavigationState], ($$navigationState) =>
  $$navigationState.get('uiBackFromTreatmentRecs'),
)

export const getWordCloud = createSelector([getTriageState], ($$triageState) => $$triageState.get('wordCloud'))

export const getSearcherInfo = createSelector([getTriageState], ($$triageState) => $$triageState.get('searcherInfo'))

export const getSearcherType = createSelector([getSearcherInfo], ($$searcherInfo) => $$searcherInfo?.get('type'))

export const getSearcherDOB = createSelector([getSearcherInfo], ($$searcherInfo) => $$searcherInfo?.get('dob'))

// get searcher's age
export const getSearcherAge = createSelector([getSearcherInfo], ($$searcherInfo) => $$searcherInfo?.get('age'))

// check if the searcher's age falls within the range of a teen
export const getIsSearcherTeen = createSelector([getSearcherAge], (age) => {
  return age ? isAgeTeen(age) : false
})

const getSearcherName = createSelector([getSearcherInfo], ($$searcherInfo) => $$searcherInfo?.get('name'))

export const getSearcherChildName = createSelector([getSearcherType, getSearcherName], (searcherType, searcherName) => {
  const searcherTypeIsChild = searcherType !== SearcherType.ADULT
  return searcherTypeIsChild ? searcherName : ''
})

export const getLoadingWordCloud = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('loadingWordCloud'),
)

export const getFetchedMetadataVersion = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('fetchedMetadataVersion'),
)

export const getIsChildEAPSessionCountExhausted = createSelector(
  [getChildLyraId, getMinorsSessionUsages, getNumberOfSessions, getNumberOfSessionsByProgram],
  (childLyraId, minorsSessionUsages, numberOfSessions, numberOfSessionsByProgram) =>
    childLyraId
      ? getIsMinorEAPSessionExhausted(
          minorsSessionUsages.toJS() ?? [],
          childLyraId,
          numberOfSessions,
          numberOfSessionsByProgram,
        )
      : undefined,
)

export const getIsChildEAPSessionCountExhaustedByChildId = (childLyraId: string | undefined) =>
  createSelector(
    [getMinorsSessionUsages, getNumberOfSessions, getNumberOfSessionsByProgram],
    (minorsSessionUsages, numberOfSessions, numberOfSessionsByProgram) =>
      childLyraId
        ? getIsMinorEAPSessionExhausted(
            minorsSessionUsages.toJS() ?? [],
            childLyraId,
            numberOfSessions,
            numberOfSessionsByProgram,
          )
        : undefined,
  )

export const getPreselectedTriageData = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('preselectedTriageData')?.toJS(),
)

export const getAllTriageSearchesState = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('getAllTriageSearchesState'),
)

export const getProgramPreference = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('programPreference')?.toJS(),
)

export const getIsSearchToolkitAware = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('isSearchToolkitAware'),
)

export const getTreatmentRecsSkipped = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('treatmentRecsSkipped'),
)

export const getToolkitSubTopic = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('toolkitSubTopic'),
)

export const getPendingGenAiRequests = createSelector([getTriageState], ($$triageState) =>
  $$triageState.get('pendingGenAiRequests')?.toJS(),
)
